import {BasketModel} from '../basket/model';
import {AddressSchema, CardSchema, ChannelSchema, LicenceSchema, OrderSchema, TrackSchema} from '../../types/schema';
import {computed, observable} from 'mobx';
import {AuthModel} from '../auth/model';
import {CouponModel} from '../coupon/model';
import {getTrackPrice, replaceValues} from '../../lib/helpers';
import {ContentModel} from '../content/model';
import {PageCheckoutAddCardModel} from '../../components/page-checkout-add-card';
import {createCheckoutCardForm, createCheckoutCouponForm, createVatNumberForm} from '../../lib/form';
import {PageCheckoutVatNumberModel} from '../../components/page-checkout-vat-number';
import {PageCheckoutCouponCodeModel} from '../../components/page-checkout-coupon-code';
import {PageCheckoutPanelsModel} from '../../components/page-checkout-panels';
import {formatCurrency} from '../../lib/currency';

export class CheckoutModel implements PageCheckoutPanelsModel {
  constructor(
    private basket: BasketModel,
    private auth: AuthModel,
    private couponModel: CouponModel,
    private content: ContentModel,
  ) {}

  @computed get basketTracks(): Array<TrackSchema> {
    return this.basket.tracks || [];
  }

  @observable inProgress: boolean = false;

  @observable globalError: string;

  @computed get maxStep(): number {

    return 3;
    // switch (true) {
    //   case (this.order && this.order.status === 'paid'):
    //     return 0;
    //
    //   case (!!this.order):
    //     return 2;
    //
    //   default: return 1;
    // }
  }

  @observable
  step: number = 0;

  @computed get currency(): string {
    if (!this.channel) return 'USD';
    return this.order
      ? this.order.locale.currency
      : this.channel.ratecard.currency;
  };

  @observable
  loading: boolean = false;

  @computed get channel(): ChannelSchema {
    return this.basket.channel;
  }

  @computed get channelId(): string {
    return this.channel ? this.channel.id : null;
  }

  @computed get userId(): string {
    return this.auth.user ? this.auth.user.identity : null;
  }

  @computed get basketId(): string {
    return this.basket.identity;
  }

  @computed get tracks(): Array<TrackSchema> {
    return this.order && this.step > 1
      ? this.order.licences.map(({ track, brand_sponsored, branded_content }) => ({
        ...track,
        basket: {
          quantity: 1,
          brand_sponsored,
          branded_content
        }
      }))
      : this.basketTracks;
  }

  @computed get licences(): Array<LicenceSchema> {
    return this.order
      ? this.order.licences
      : []
  }

  // Total price of basket
  @computed get total(): string {
    return this.order
      ? this.order.amount.total
      : (parseFloat(this.netTotal) - this.discountAmount).toFixed(2);
  }

  @computed get netTotal(): string {
    if (!this.channel) return '0.00';

    return this.basket.total.toFixed(2);
  }

  // Price per track
  @computed get price(): number {
    if (this.order && this.order.licences.length) {
      return this.order.licences[0].amount;
    }

    if (!this.channel)
      return 8;

    switch (this.couponType) {
      case 'first_use':
        return 0;

      case 'internal':
      case 'second_purchase':
      case 'support_coupon':
        return parseInt(this.channel.ratecard.value) * ((100 - this.discount) / 100);

      default:
        return parseInt(this.channel.ratecard.value);
    }
  }

  @computed get priceBrandSponsored(): number {
    return 80;
  }

  @computed get prices(): Array<number> {
    if (!this.channel) return [];
    if (this.order) return this.order.licences.map(l => l.amount);

    return this.basketTracks.map(track => getTrackPrice(track, this.basket.ratecard, this.basket.ratecardBrandSponsored))
  }

  // Price band string e.g. 50k
  @computed get band(): string {
    return this.channel.ratecard.name;
  }

  @computed
  get expanded(): boolean {
    return this.step === 1;
  }

  @observable
  cards: Array<CardSchema> = [];

  @observable
  selectedCard: string = null;

  @computed get card(): CardSchema {
    return this.selectedCard
      ? this.cards.find(c => c.identity === this.selectedCard)
      : null
  }

  @observable
  addresses: Array<AddressSchema> = [];

  @observable
  address: AddressSchema = void 0;

  @observable
  addressError: string = null;

  @computed get addressParity(): boolean {
    if (!this.card || !this.address) return false;

    return !Object.keys(this.address).find(k => this.address[k] !== this.card.address[k]);
  }

  @observable
  order: OrderSchema = null;

  @computed get orderId(): string {
    return this.order ? this.order.identity : null;
  }

  @observable
  showAddCard: boolean = false;

  @observable
  addCardForm: PageCheckoutAddCardModel = createCheckoutCardForm();

  @observable
  vatNumber: string = '';

  get defaultVatNumber() {
    return this.vatNumber || (this.auth.user && this.auth.user.vat.number
      ? (this.auth.user.vat.country || '') + (this.auth.user.vat.number || '')
      : ''
    );
  }

  vatNumberForm: PageCheckoutVatNumberModel = createVatNumberForm();

  @computed
  get vatNumberChanged() {
    if (!this.auth.user) return false;
    const { number } = this.auth.user.vat;
    const { value } = this.vatNumberForm.fields.vatNumber;

    return !(!number && !value);
  }

  @computed
  get coupon(): string {
    return this.couponModel.code;
  };

  @computed
  get couponType(): string {
    return this.couponModel.type;
  }

  @computed
  get couponValue(): number {
    return this.couponModel.value;
  }

  @computed
  get discount(): number {
    return this.couponModel.discount;
  }

  @computed
  get discountAmount(): number {
    if (this.order && this.order.amount && typeof this.order.amount.discount !== 'undefined') {
      return parseFloat(this.order.amount.discount);
    }

    if (this.couponModel.value) {
      return this.couponModel.value
    }

    if (!this.couponModel.discount) {
      return 0;
    }

    switch (this.couponType) {
      case 'internal':
      case 'second_purchase':
      case 'support_coupon':
        return this.basket.total * (this.discount / 100);

      default:
        return 0;
    }
  }

  @computed
  get defaultCoupon() {
    return this.coupon || (this.auth.user ? this.auth.user.default_coupon : '');
  }

  @computed
  get couponText(): {title: string; message: string;} {
    if (!this.couponModel.title && !this.couponModel.message) {
      return null;
    }

    const values = {
      discount: this.couponValue
        ? formatCurrency(this.couponValue, this.currency)
        : this.discount
          ? parseInt(this.discount as any).toString() + '%'
          : void 0
    };

    return {
      title: replaceValues(this.couponModel.title, values),
      message: replaceValues(this.couponModel.message, values)
    }
  };

  @observable
  couponForm: PageCheckoutCouponCodeModel = createCheckoutCouponForm();

  @computed
  get isBeta(): boolean {
    return this.order && parseInt(this.order.amount.total) === 0;
  }

  @computed
  get vatRate(): string {
    return this.order
      ? this.order.amount.vat.rate
      : null;
  }

  @computed
  get showNoChargeMessage(): boolean {
    return !this.card && this.order && parseInt(this.order.amount.total) === 0;
  }

  @computed
  get brandedContentWarning(): string {
    return this.content.brandedContentWarning;
  }

  @computed
  get hasUmgContent(): boolean {
    for (let i = 0; i < this.tracks.length; i++) {
      if (this.tracks[i].reporting_party !== undefined && this.tracks[i].reporting_party.slug === "umg") {
        return true;
      }
    }
    return false;
  }
}